import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  getLanguage,
  isSupportedLanguage,
  localizePath,
  stripLanguage,
} from "./languages";

// <link> tags that tell search engines about each language version of a page
function updateAlternateLinks(path) {
  document
    .querySelectorAll('link[data-i18n-alternate], link[rel="canonical"]')
    .forEach((link) => link.remove());

  const origin = window.location.origin;
  const addLink = (attributes) => {
    const link = document.createElement("link");
    Object.entries(attributes).forEach(([key, value]) =>
      link.setAttribute(key, value)
    );
    document.head.appendChild(link);
  };

  LANGUAGES.forEach(({ code }) =>
    addLink({
      rel: "alternate",
      hreflang: code,
      href: origin + localizePath(path, code),
      "data-i18n-alternate": "",
    })
  );
  addLink({
    rel: "alternate",
    hreflang: "x-default",
    href: origin + path,
    "data-i18n-alternate": "",
  });
}

// Plus Jakarta Sans has no Arabic letters; load the Arabic font only when needed
const ARABIC_FONT =
  "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@300;400;500;600;700&display=swap";
function loadArabicFont() {
  if (document.getElementById("arabic-font")) return;
  const link = document.createElement("link");
  link.id = "arabic-font";
  link.rel = "stylesheet";
  link.href = ARABIC_FONT;
  document.head.appendChild(link);
}

export default function LanguageLayout() {
  const { lang } = useParams();
  const { pathname, search, hash } = useLocation();
  const { t, i18n } = useTranslation("common");

  // English has no prefix: /en/about -> /about. Unknown prefixes go home.
  const invalidPrefix = lang !== undefined && !isSupportedLanguage(lang);
  const redundantPrefix = lang === DEFAULT_LANGUAGE;
  const language = lang && !invalidPrefix ? lang : DEFAULT_LANGUAGE;

  useEffect(() => {
    if (i18n.resolvedLanguage !== language) i18n.changeLanguage(language);
  }, [language, i18n]);

  const ready = i18n.resolvedLanguage === language;

  useEffect(() => {
    if (!ready) return;
    const { locale, dir } = getLanguage(language);
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
    if (language === "ar") loadArabicFont();
    document.title = t("meta.title");
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", t("meta.description"));
    updateAlternateLinks(stripLanguage(pathname));
  }, [ready, language, pathname, t]);

  if (invalidPrefix) return <Navigate to="/" replace />;
  if (redundantPrefix) {
    const withoutPrefix = pathname.slice(DEFAULT_LANGUAGE.length + 1) || "/";
    return <Navigate to={withoutPrefix + search + hash} replace />;
  }

  return <Outlet />;
}
